import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Trophy, Camera, ThumbsUp, Upload, Star, Calendar, Users, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TruckCompetition = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [winners, setWinners] = useState([]);
  const [currentLeader, setCurrentLeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [votingId, setVotingId] = useState(null);
  const [photoImages, setPhotoImages] = useState({});
  const [myVote, setMyVote] = useState(null); // Track user's current vote
  
  const [uploadForm, setUploadForm] = useState({
    submitter_name: '',
    submitter_phone: '',
    submitter_email: '',
    company_name: '',
    truck_description: '',
    photo_base64: '',
    photo_filename: '',
    terms_accepted: false
  });

  // Pre-fill if user is logged in
  useEffect(() => {
    const userEmail = localStorage.getItem('user_email');
    const userName = localStorage.getItem('user_name');
    if (userEmail) {
      setUploadForm(prev => ({
        ...prev,
        submitter_email: userEmail,
        submitter_name: userName || ''
      }));
    }
  }, []);

  useEffect(() => {
    fetchCompetitionData();
  }, []);

  const fetchCompetitionData = async () => {
    setLoading(true);
    try {
      const [photosRes, winnersRes, leaderRes, myVoteRes] = await Promise.all([
        axios.get(`${API}/api/competition/photos`),
        axios.get(`${API}/api/competition/winners?limit=6`),
        axios.get(`${API}/api/competition/current-leader`),
        axios.get(`${API}/api/competition/my-vote`)
      ]);
      
      setPhotos(photosRes.data.photos || []);
      setWinners(winnersRes.data.winners || []);
      setCurrentLeader(leaderRes.data);
      setMyVote(myVoteRes.data);
      
      // Load images for all photos
      for (const photo of photosRes.data.photos || []) {
        loadPhotoImage(photo.id);
      }
    } catch (error) {
      console.error('Error fetching competition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPhotoImage = async (photoId) => {
    try {
      const res = await axios.get(`${API}/api/competition/photos/${photoId}/image`);
      if (res.data.image) {
        setPhotoImages(prev => ({ ...prev, [photoId]: res.data.image }));
      }
    } catch (error) {
      console.error('Error loading photo image:', error);
    }
  };

  const handleUploadChange = (e) => {
    const { name, value } = e.target;
    setUploadForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      setUploadForm(prev => ({
        ...prev,
        photo_base64: base64,
        photo_filename: file.name
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPhoto = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.submitter_name || !uploadForm.submitter_phone || !uploadForm.photo_base64) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!uploadForm.terms_accepted) {
      toast.error('Please accept the terms and conditions to submit your photo');
      return;
    }
    
    setSubmitting(true);
    try {
      const { terms_accepted, ...submitData } = uploadForm;
      await axios.post(`${API}/api/competition/photos`, submitData);
      toast.success('Photo submitted successfully! Good luck!');
      setShowUploadForm(false);
      setUploadForm({
        submitter_name: uploadForm.submitter_name,
        submitter_phone: uploadForm.submitter_phone,
        submitter_email: uploadForm.submitter_email,
        company_name: uploadForm.company_name,
        truck_description: '',
        photo_base64: '',
        photo_filename: '',
        terms_accepted: false
      });
      fetchCompetitionData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit photo';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (photoId) => {
    setVotingId(photoId);
    try {
      const res = await axios.post(`${API}/api/competition/photos/${photoId}/vote`);
      if (res.data.changed) {
        toast.success('Vote changed! Your vote has been moved to this truck.');
      } else if (myVote?.voted_photo_id === photoId) {
        toast.info('You have already voted for this truck!');
      } else {
        toast.success('Vote recorded! Thanks for participating!');
      }
      fetchCompetitionData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to vote';
      toast.error(errorMsg);
    } finally {
      setVotingId(null);
    }
  };

  const getMonthName = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87] rounded-2xl p-8 mb-8 text-white text-center">
          <div className="flex justify-center mb-4">
            <Trophy className="w-16 h-16 text-yellow-400" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Australia's Sexiest Truck of the Month</h1>
          <p className="text-xl text-blue-200 mb-6">
            Submit your truck photo and let Australia vote for the best!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setShowUploadForm(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-3"
            >
              <Camera className="w-5 h-5 mr-2" />
              Submit Your Truck
            </Button>
          </div>
          
          {/* Competition Stats */}
          {currentLeader && (
            <div className="grid grid-cols-3 gap-4 mt-8 max-w-md mx-auto">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-2xl font-bold">{currentLeader.total_entries || 0}</p>
                <p className="text-sm text-blue-200">Entries</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-2xl font-bold">{currentLeader.total_votes || 0}</p>
                <p className="text-sm text-blue-200">Total Votes</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-2xl font-bold">{currentLeader.leader?.votes || 0}</p>
                <p className="text-sm text-blue-200">Leading Votes</p>
              </div>
            </div>
          )}
        </div>

        {/* Upload Form Modal */}
        {showUploadForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Submit Your Truck Photo</h2>
              
              <form onSubmit={handleSubmitPhoto} className="space-y-4">
                <div>
                  <Label htmlFor="submitter_name">Your Name *</Label>
                  <Input
                    id="submitter_name"
                    name="submitter_name"
                    value={uploadForm.submitter_name}
                    onChange={handleUploadChange}
                    required
                    placeholder="John Smith"
                  />
                  <p className="text-xs text-gray-500 mt-1">Will be displayed if you win</p>
                </div>
                
                <div>
                  <Label htmlFor="submitter_phone">Phone Number *</Label>
                  <Input
                    id="submitter_phone"
                    name="submitter_phone"
                    type="tel"
                    value={uploadForm.submitter_phone}
                    onChange={handleUploadChange}
                    required
                    placeholder="04XX XXX XXX"
                  />
                  <p className="text-xs text-gray-500 mt-1">Private - only visible to admins</p>
                </div>
                
                <div>
                  <Label htmlFor="submitter_email">Email (Optional)</Label>
                  <Input
                    id="submitter_email"
                    name="submitter_email"
                    type="email"
                    value={uploadForm.submitter_email}
                    onChange={handleUploadChange}
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="company_name">Company Name (Optional)</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={uploadForm.company_name}
                    onChange={handleUploadChange}
                    placeholder="Your Company Pty Ltd"
                  />
                  <p className="text-xs text-gray-500 mt-1">Will be displayed if you win</p>
                </div>
                
                <div>
                  <Label htmlFor="truck_description">Truck Description *</Label>
                  <Input
                    id="truck_description"
                    name="truck_description"
                    value={uploadForm.truck_description}
                    onChange={handleUploadChange}
                    placeholder="e.g., 2022 Kenworth T610"
                  />
                </div>
                
                <div>
                  <Label htmlFor="photo">Truck Photo *</Label>
                  <Input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 5MB, JPG/PNG format</p>
                  
                  {uploadForm.photo_base64 && (
                    <div className="mt-2">
                      <img 
                        src={`data:image/jpeg;base64,${uploadForm.photo_base64}`}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}
                </div>

                {/* Terms and Conditions */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms_accepted"
                      checked={uploadForm.terms_accepted}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, terms_accepted: e.target.checked }))}
                      className="mt-1 w-5 h-5 rounded border-gray-300"
                    />
                    <label htmlFor="terms_accepted" className="text-sm text-gray-700">
                      <strong>I confirm and agree to the following:</strong>
                      <ul className="mt-2 space-y-1 list-disc list-inside text-xs text-gray-600">
                        <li>I am the owner of this photo or have obtained the necessary rights and permissions to submit it</li>
                        <li>The photo does not contain any lewd, rude, indecent content or nudity</li>
                        <li>I accept full liability for the photo content and indemnify Truckn Send It from any claims arising from this submission</li>
                        <li>I grant Truckn Send It a non-exclusive, royalty-free licence to use, reproduce, and display this photo on their website, social media accounts, and marketing materials</li>
                        <li>I understand this licence is granted in accordance with Australian Copyright Act 1968 and related media use laws</li>
                      </ul>
                    </label>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {submitting ? 'Submitting...' : 'Submit Entry'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowUploadForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Current Month's Entries */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6" />
            {getMonthName(currentLeader?.competition_month)} Entries
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p>Loading entries...</p>
            </div>
          ) : photos.length === 0 ? (
            <Card className="p-12 text-center">
              <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No entries yet this month</h3>
              <p className="text-gray-500 mb-4">Be the first to submit your truck!</p>
              <Button onClick={() => setShowUploadForm(true)} className="bg-[#1e3a5f]">
                Submit Your Truck
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((photo, index) => (
                <Card key={photo.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Rank Badge */}
                  {index < 3 && (
                    <div className={`absolute top-3 left-3 z-10 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-700'
                    }`}>
                      {index + 1}
                    </div>
                  )}
                  
                  {/* Photo */}
                  <div className="relative h-64 bg-gray-200">
                    {photoImages[photo.id] ? (
                      <img
                        src={`data:image/jpeg;base64,${photoImages[photo.id]}`}
                        alt={photo.truck_description || 'Truck entry'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="p-4">
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">
                      {photo.truck_description || 'Aussie Truck'}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">
                      by {photo.submitter_name}
                      {photo.company_name && ` - ${photo.company_name}`}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-gray-600">
                        <ThumbsUp className="w-5 h-5" />
                        <span className="font-semibold">{photo.votes}</span>
                        <span className="text-sm">votes</span>
                      </div>
                      
                      {myVote?.voted_photo_id === photo.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                            <Check className="w-4 h-4" /> Your vote
                          </span>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleVote(photo.id)}
                          disabled={votingId === photo.id}
                          className={`${myVote?.has_voted ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                          size="sm"
                          data-testid={`vote-btn-${photo.id}`}
                        >
                          {votingId === photo.id ? 'Voting...' : myVote?.has_voted ? (
                            <><RefreshCw className="w-4 h-4 mr-1" /> Change Vote</>
                          ) : 'Vote'}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Past Winners - Hall of Fame */}
        {winners.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              Hall of Fame - Past Winners
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {winners.map((winner) => (
                <Card key={winner.id} className="overflow-hidden border-2 border-yellow-400">
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-2 text-center">
                    <span className="font-bold text-black">{getMonthName(winner.competition_month)} Winner</span>
                  </div>
                  
                  <div className="relative h-48 bg-gray-200">
                    {winner.photo_base64 ? (
                      <img
                        src={`data:image/jpeg;base64,${winner.photo_base64}`}
                        alt={winner.truck_description || 'Winner'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Trophy className="w-12 h-12 text-yellow-500" />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 text-center">
                    <h3 className="font-bold text-[#1e3a5f]">{winner.winner_name}</h3>
                    {winner.company_name && (
                      <p className="text-sm text-gray-600">{winner.company_name}</p>
                    )}
                    {winner.truck_description && (
                      <p className="text-sm text-gray-500 mt-1">{winner.truck_description}</p>
                    )}
                    <div className="mt-2 flex items-center justify-center gap-1 text-yellow-600">
                      <Star className="w-4 h-4 fill-yellow-500" />
                      <span className="font-semibold">{winner.total_votes} votes</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Competition Rules */}
        <Card className="mt-12 p-6 bg-blue-50 border-blue-200">
          <h3 className="text-lg font-bold text-[#1e3a5f] mb-4">Competition Rules</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Submit as many photos as you like each month</li>
            <li>• Anyone can vote - no login required</li>
            <li>• <strong>You can only vote once per month</strong> - but you can change your vote anytime!</li>
            <li>• Competition ends at midnight on the last day of each month</li>
            <li>• Winner is announced on the 1st of the following month</li>
            <li>• Winner's name/company and photo will be displayed on our homepage and Hall of Fame</li>
            <li>• Your contact details (phone/email) are never shown publicly</li>
          </ul>
          
          <h4 className="text-md font-bold text-[#1e3a5f] mt-6 mb-2">Photo Submission Terms</h4>
          <ul className="space-y-1 text-xs text-gray-600">
            <li>• You must own the photo or have rights to submit it</li>
            <li>• No lewd, rude, indecent content or nudity</li>
            <li>• By submitting, you grant Truckn Send It permission to use the photo</li>
            <li>• You accept liability for your submission content</li>
          </ul>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default TruckCompetition;
